import { User, Users, Building } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import type { AccountType } from '@/lib/api/services/onboarding'

interface AccountTypeOption {
  type: AccountType
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  features: string[]
}

const ACCOUNT_OPTIONS: AccountTypeOption[] = [
  {
    type: 'Personal',
    title: 'Personal',
    description: 'For individual use',
    icon: User,
    features: [
      'Single user workspace',
      'Personal templates library',
      'Basic AI assistance',
      'Essential integrations'
    ]
  },
  {
    type: 'Team',
    title: 'Team',
    description: 'For small teams and departments',
    icon: Users,
    features: [
      'Up to 25 team members',
      'Shared template library',
      'Collaboration tools',
      'Team analytics'
    ]
  },
  {
    type: 'Enterprise',
    title: 'Enterprise',
    description: 'For organizations at scale',
    icon: Building,
    features: [
      'Unlimited team members',
      'Advanced security & SSO',
      'Custom integrations',
      'Dedicated support'
    ]
  }
]

interface AccountTypeStepProps {
  onSelect: (type: AccountType) => void
  selectedType?: AccountType
  disabled?: boolean
}

export function AccountTypeStep({ onSelect, selectedType, disabled }: AccountTypeStepProps) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-slate-900 mb-2">
          How will you use Templatean?
        </h2>
        <p className="text-slate-600">
          Choose the account type that best fits your needs. You can upgrade anytime.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {ACCOUNT_OPTIONS.map((option) => {
          const Icon = option.icon
          const isSelected = selectedType === option.type

          return (
            <Card
              key={option.type}
              className={`cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => !disabled && onSelect(option.type)}
            >
              <CardHeader className="text-center pb-2">
                <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center ${
                  isSelected ? 'bg-primary text-primary-foreground' : 'bg-slate-100'
                }`}>
                  <Icon className="h-6 w-6" />
                </div>
                <CardTitle className="text-lg">{option.title}</CardTitle>
                <CardDescription>{option.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {option.features.map((feature, index) => (
                    <li key={index} className="text-sm text-slate-600 flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {selectedType && (
        <div className="flex justify-center pt-4">
          <Button
            onClick={() => onSelect(selectedType)}
            disabled={disabled}
            size="lg"
          >
            Continue with {selectedType}
          </Button>
        </div>
      )}
    </div>
  )
}
